import type { ButtressDbService } from '@buttress/crag';

import type Company from '../../models/company.js';
import type { DataGrant, DataRecord, DataSet, DataType } from '../../models/company-data.js';
import type Person from '../../models/person.js';
import type Team from '../../models/team.js';
import { latestVersion } from './definition.js';
import { isSignatoriesTeam } from '../../helpers/signatories-team.js';
import { lackedPermissions } from '../../helpers/permissions.js';

export async function loadSets(db: ButtressDbService, companyId: string): Promise<DataSet[]> {
  const result = await db.query('dataSet', { ownerCompanyId: { $eq: companyId } });
  return result.results as unknown as DataSet[];
}

export async function loadSet(db: ButtressDbService, setId: string): Promise<DataSet | undefined> {
  // Busted, so a set whose grant was just ended isn't answered from the cache
  const result = await db.query('dataSet', { id: { $eq: setId } }, { bust: true });
  return (result.results as unknown as DataSet[])[0];
}

export async function loadLatestType(db: ButtressDbService, typeId: string): Promise<DataType | undefined> {
  const result = await db.query('dataType', { typeId: { $eq: typeId } });
  return latestVersion(result.results as unknown as DataType[]);
}

export async function loadTypes(db: ButtressDbService, companyId: string): Promise<DataType[]> {
  const result = await db.query('dataType', { ownerCompanyId: { $eq: companyId } });
  const byId = new Map<string, DataType[]>();
  for (const type of result.results as unknown as DataType[])
    byId.set(type.typeId, [...(byId.get(type.typeId) ?? []), type]);
  return [...byId.values()].map((versions) => latestVersion(versions)!);
}

export async function loadTeams(db: ButtressDbService, companyId: string): Promise<Team[]> {
  const result = await db.query('team', { companyId: { $eq: companyId } });
  return result.results as unknown as Team[];
}

// The teams a company's own tools may bind: none the server keeps (nodeStream's own, permission and signatories teams)
export async function loadBindableTeams(db: ButtressDbService, companyId: string): Promise<Team[]> {
  const [teams, company] = await Promise.all([
    loadTeams(db, companyId),
    db.getById('company', companyId) as Promise<Company | undefined>,
  ]);
  return teams.filter((team) => team.system !== true && !team.permission && !isSignatoriesTeam(team.id, company));
}

export async function loadColleagues(db: ButtressDbService, companyId: string): Promise<Person[]> {
  const result = await db.query('people', { companyId: { $eq: companyId } });
  return result.results as unknown as Person[];
}

export async function loadRecord(db: ButtressDbService, recordId: string): Promise<DataRecord | undefined> {
  const result = await db.query('dataRecord', { id: { $eq: recordId } }, { bust: true });
  return (result.results as unknown as DataRecord[])[0];
}

// Standing grants only: a job's access rows belong to its job, not to the company's sharing
export async function loadGrants(db: ButtressDbService, query: Record<string, unknown>): Promise<DataGrant[]> {
  const result = await db.query(
    'dataGrant',
    { ...query, status: { $eq: 'ACTIVE' }, scope: { $in: ['SET', 'RECORD'] } },
    { bust: true },
  );
  return result.results as unknown as DataGrant[];
}

export async function loadCompanyNames(db: ButtressDbService, ids: string[]): Promise<Map<string, string>> {
  if (ids.length === 0) return new Map();
  const result = await db.query('company', { id: { $in: [...new Set(ids)] } });
  return new Map((result.results as unknown as Company[]).map((company) => [company.id, company.name]));
}

// The lambdas' rule (canEditRecords): DATA_MANAGE holders, or members of the set's editors team while the company has Data Studio
export function canEditSet(
  me: Person | undefined,
  set: DataSet,
  teams: Team[],
  dataManage: boolean,
  features: readonly string[] | null | undefined,
): boolean {
  if (!me || me.companyId !== set.ownerCompanyId) return false;
  if (dataManage) return true;
  if (lackedPermissions(features).has('DATA_MANAGE')) return false;
  const team = teams.find((t) => t.id === set.editorsTeamId);
  return Boolean(team?.memberIds?.includes(me.id));
}
